import { GlobalBus } from '@services/bus'
import { CREDITOS as CREDITOS_BUS, MODAL } from '@constants/Text'
import { CREDITOS } from '@modules/ai-framework/constants/copy'
import {
  numeroOuNulo,
  saldoBaixo,
  saldoEsgotado,
} from '@modules/ai-framework/utils/creditos'
import { SALDO_ESGOTADO } from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  avisoDeSaldoBaixoDispensado,
  dispensarAvisoDeSaldoBaixo,
} from '@modules/aawz-intelligence-v2/utils/dispensa-do-aviso-de-saldo-baixo'

// A fatia que o rodapé da lateral já carrega (`ai-framework/index.js` registra no
// BOOT, então o getter existe fora do módulo — e some sem quebrar onde não existe).
const STORE_CREDITOS = 'aiFramework/creditos'

// [SHOULD-6, review do PR #2261] Escritório da sessão, para carimbar a chave
// da dispensa — mesmo caminho de `central-config/utils/guide-storage.js::
// guideScopeFromStore`. Função solta (não `computed`) porque `data()` do
// mixin precisa dela ANTES de `computed` existir em `this`.
function idEscritorioDoAviso(vm) {
  const user = (vm.$currentUser && vm.$currentUser()) || {}
  const escritorio = user.escritorio || {}
  return escritorio.id_interno
}

/**
 * Créditos no composer da `view-chat` (14/09/2026): dois estados, uma fonte,
 * UMA faixa (o slot `cabecalho` do composer — `composer-aviso`).
 *
 * - **Esgotado** (`saldoEsgotado`, a régua do gate do backend — `<= 0`): a faixa
 *   "Seus créditos acabaram…", o placeholder alternativo e o envio travado
 *   (`envioBloqueado`). É o BLOQUEIO — sem "×".
 * - **Baixo** (`saldoBaixo`, `0 < saldo <= LIMIAR_SALDO_BAIXO`, 5): a faixa
 *   "Seus créditos estão acabando…" com "×". Não bloqueia nada; fechar a
 *   DISPENSA por 7 dias (`localStorage`, `utils/dispensa-do-aviso-de-saldo-baixo.js`)
 *   — passado o prazo, volta sozinha. ★ [15/09/2026] Morava na linha do hint
 *   ("Créditos acabando" ao lado de "Enter para enviar") e o usuário achou
 *   poluído: dois textos disputando a mesma linha.
 *
 * ★ Duas fontes convergem no MESMO computed (`semCreditos`): o número da store
 * e o sinal do 402 (`CREDITOS.CHAMAR_ATENCAO`, emitido por
 * `apiChat._desfechoDeSaldoEsgotado`). O sinal existe porque o saldo da store
 * pode estar STALE no instante da recusa — a releitura só chega no fecho do
 * stream (`_avisarQueOSaldoMudou`) — e o composer não pode esperar por ela
 * para dizer que travou. O flag do sinal SOLTA sozinho quando um saldo positivo
 * chega (comprou créditos): o número volta a mandar.
 *
 * Tudo atrás da prop opt-in `comAvisoDeCreditos` — só a superfície que tem o
 * rodapé de saldo (a imersiva, `sidebarComoNavegacao`) passa; popup, painel do
 * Hub, Fee-Based e ai-framework ficam byte-idênticos.
 */
export const avisoDeCreditosMixin = {
  props: {
    comAvisoDeCreditos: { type: Boolean, default: false },
  },
  data() {
    return {
      // O último turno foi recusado por saldo (402) e o saldo relido ainda não chegou.
      bloqueadoPeloUltimoTurno: false,
      // A dispensa do aviso de saldo baixo (lida uma vez, ao montar; o "×" a
      // liga e grava). Reativo para a faixa fechar sem F5. Carimbada por
      // escritório (`idEscritorioDoAviso()`) — ver o comentário do util.
      // ⚠️ NÃO ler de um `computed` aqui: `data()` roda antes de `computed`
      // no `initState` do Vue 2, e o getter ainda não existiria em `this`.
      avisoDeSaldoBaixoDispensado: avisoDeSaldoBaixoDispensado({
        idEscritorio: idEscritorioDoAviso(this),
      }),
    }
  },
  computed: {
    // A string de 6 casas do contrato (ADR-007), ou `null` sem número confirmado.
    saldoDeCreditosBruto() {
      const saldo = this.$store.getters[STORE_CREDITOS + '/saldoCreditos']
      if (!saldo || saldo.creditos === undefined || saldo.creditos === null)
        return null
      return saldo.creditos
    },
    semCreditos() {
      if (!this.comAvisoDeCreditos) return false
      return (
        saldoEsgotado(this.saldoDeCreditosBruto) ||
        this.bloqueadoPeloUltimoTurno
      )
    },
    // A faixa do slot `cabecalho` (`composer-aviso`). Tem PRECEDÊNCIA sobre a
    // `composer-atencao` (sugestões a triar). Esgotado vence baixo — não se
    // avisa duas vezes — e é o único sem `fechavel`.
    avisoDeCreditos() {
      if (this.semCreditos) {
        return {
          texto: SALDO_ESGOTADO.AVISO,
          acao: SALDO_ESGOTADO.ACAO,
          fechavel: false,
        }
      }
      if (!this.mostraAvisoDeSaldoBaixo) return null
      return {
        texto: CREDITOS.SALDO_BAIXO_AVISO,
        acao: CREDITOS.TITULO,
        fechavel: true,
      }
    },
    rotuloDeFecharAviso() {
      return CREDITOS.SALDO_BAIXO_FECHAR
    },
    mostraAvisoDeSaldoBaixo() {
      if (!this.comAvisoDeCreditos || this.avisoDeSaldoBaixoDispensado)
        return false
      return saldoBaixo(this.saldoDeCreditosBruto)
    },
    placeholderDoComposer() {
      return this.semCreditos
        ? SALDO_ESGOTADO.PLACEHOLDER
        : this.personaAtual.placeholder
    },
  },
  watch: {
    // Saldo positivo confirmado solta o flag do 402: o número volta a mandar.
    saldoDeCreditosBruto(bruto) {
      const numero = numeroOuNulo(bruto)
      if (numero !== null && numero > 0) this.bloqueadoPeloUltimoTurno = false
    },
  },
  created() {
    if (!this.comAvisoDeCreditos) return
    GlobalBus.$on(CREDITOS_BUS.CHAMAR_ATENCAO, this.bloquearPeloSaldo)
  },
  beforeDestroy() {
    GlobalBus.$off(CREDITOS_BUS.CHAMAR_ATENCAO, this.bloquearPeloSaldo)
  },
  methods: {
    bloquearPeloSaldo() {
      this.bloqueadoPeloUltimoTurno = true
    },
    // O "×" da faixa de saldo baixo: some agora (reativo) e por 7 dias (storage).
    fecharAvisoDeSaldoBaixo() {
      dispensarAvisoDeSaldoBaixo({ idEscritorio: idEscritorioDoAviso(this) })
      this.avisoDeSaldoBaixoDispensado = true
    },
    // A MESMA porta do rodapé da lateral, do Ctrl+K e da bolha do 402: o modal
    // faz o próprio `carregarSaldo` — o canal é o contrato inteiro, sem payload.
    abrirCreditos() {
      GlobalBus.$emit(MODAL.AI_FRAMEWORK_CREDITOS)
    },
  },
}
